import { TrophyIcon } from "@/assets/images/svgIcons/trophyIcon"
import { InputContainer } from "@/components/ui/forms/inputContainer"
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/baseUi/input-group"
import { Switch } from "@/components/ui/baseUi/switch"
import { Text } from "@/components/ui/baseUi/text"
import type {
  ScoreFormikType,
  ScoreFormikValueInputKey,
  ScoreSetCompletionFormikType,
} from "@/modules/scores/scoreForms/scoreFormikTypes"
import type { SessionBlockExerciseEntity } from "@/modules/sessions/entities/sessionBlockExerciseEntity"
import { HUChronoInput } from "@/modules/ui/components/huChronoInput"
import { type SimpleFormik } from "@/typings/formik"
import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage } from "@modules/formik/formikUtils"
import { ScoreType } from "@modules/scores/scoreTypes"
import { getScoreUnit } from "@modules/scores/scoreUtils"
import { useUnit } from "@modules/utils/hooks/useUnit"
import type { FieldInputProps } from "formik/dist/types"

type Props = {
  formik: SimpleFormik<ScoreFormikType, ScoreFormikValueInputKey>
  scoreSetCompletionIndex: number
  exercise: SessionBlockExerciseEntity
  className?: string
}

export function ScoreSetCompletionInput({ scoreSetCompletionIndex, formik, exercise }: Props) {
  const unit = useUnit()

  const formikValueName = `scoreSetCompletions.${scoreSetCompletionIndex}.score`
  const formikValueAdditionalName = `scoreSetCompletions.${scoreSetCompletionIndex}.scoreAdditional`
  const formikValue = formik.values.scoreSetCompletions[scoreSetCompletionIndex] as ScoreSetCompletionFormikType

  const value = (formik.getFieldProps(formikValueName as ScoreFormikValueInputKey).value as number | null) ?? 0
  const additionalValue =
    (formik.getFieldProps(formikValueAdditionalName as ScoreFormikValueInputKey).value as number | null) ?? 0
  const setFieldValue = (value: any) => formik.setFieldValue(formikValueName as ScoreFormikValueInputKey, value)
  const setFieldValueAdditional = (value: any) =>
    formik.setFieldValue(formikValueAdditionalName as ScoreFormikValueInputKey, value)

  const isScoreSetReferenceForScoreboard = exercise.scoreSetConfigurations.find(
    (scoreSetConfiguration) => scoreSetConfiguration.id === formikValue.scoreSetConfigurationId,
  )?.useForScoring

  switch (formikValue.scoreType) {
    case ScoreType.REPS:
      return (
        <InputContainer isRequiredInput={false} error={getFormErrorMessage(formikValueName, formik)}>
          <InputGroup className="justify-between bg-surface-background pr-2" id={formikValueName}>
            <InputGroupInput
              name={formikValueName}
              placeholder="0"
              max={99999}
              type="number"
              inputMode="numeric"
              step={1}
              min={0}
              value={value.toString()}
              onChange={(e) => {
                if (e.target.value === "") setFieldValue(0)
                if (isNaN(parseInt(e.target.value))) return
                const value = parseInt(e.target.value)
                if (value > 99999) setFieldValue(99999)
                else setFieldValue(value)
              }}
              onFocus={(e) => e.target.addEventListener("wheel", (e) => e.preventDefault(), { passive: false })}
            />
            <InputGroupAddon align="inline-end" className="flex items-center gap-1">
              <Text fontStyle="BM" className="pb-0.5">
                {word("global.score.repetition.unit", { count: value })}
              </Text>
              {isScoreSetReferenceForScoreboard && <TrophyIcon color="currentColor" size={16} />}
            </InputGroupAddon>
          </InputGroup>
        </InputContainer>
      )
    case ScoreType.ROUND_AND_REPETITIONS: {
      return (
        <div className="flex w-full flex-col gap-4">
          <InputContainer isRequiredInput={false} error={getFormErrorMessage(formikValueName, formik)}>
            <InputGroup className="justify-between bg-surface-background pr-2" id={formikValueName}>
              <InputGroupInput
                name={formikValueName}
                placeholder="0"
                max={99}
                type="number"
                inputMode="numeric"
                step={1}
                min={0}
                value={value.toString()}
                onChange={(e) => {
                  if (e.target.value === "") setFieldValue(0)
                  if (isNaN(parseInt(e.target.value))) return
                  const value = parseInt(e.target.value)
                  if (value > 99) setFieldValue(99)
                  else setFieldValue(value)
                }}
                onFocus={(e) => e.target.addEventListener("wheel", (e) => e.preventDefault(), { passive: false })}
              />
              <InputGroupAddon align="inline-end" className="flex items-center gap-1">
                <Text fontStyle="BM" className="pb-0.5">
                  {word("global.score.round.unit", { count: value ?? 0 })}
                </Text>
                {isScoreSetReferenceForScoreboard && <TrophyIcon color="currentColor" size={16} />}
              </InputGroupAddon>
            </InputGroup>
          </InputContainer>

          <InputContainer isRequiredInput={false} error={getFormErrorMessage(formikValueName, formik)}>
            <InputGroup className="justify-between bg-surface-background pr-2" id={formikValueName}>
              <InputGroupInput
                name={formikValueName}
                placeholder="0"
                max={999}
                type="number"
                inputMode="numeric"
                step={1}
                min={0}
                value={additionalValue.toString()}
                onChange={(e) => {
                  if (e.target.value === "") setFieldValueAdditional(0)
                  if (isNaN(parseInt(e.target.value))) return
                  const value = parseInt(e.target.value)
                  if (value > 999) return
                  setFieldValueAdditional(value)
                }}
                onFocus={(e) => e.target.addEventListener("wheel", (e) => e.preventDefault(), { passive: false })}
              />
              <InputGroupAddon align="inline-end">
                <Text fontStyle="BM">{word("global.score.repetition.unit", { count: additionalValue ?? 0 })}</Text>
              </InputGroupAddon>
            </InputGroup>
          </InputContainer>
        </div>
      )
    }
    case ScoreType.WEIGHT:
      return (
        <InputContainer isRequiredInput={false} error={getFormErrorMessage(formikValueName, formik)}>
          <InputGroup className="justify-between bg-surface-background pr-2" id={formikValueName}>
            <InputGroupInput
              name={formikValueName}
              placeholder="0"
              max={99999}
              type="number"
              inputMode="numeric"
              step={0.01}
              min={0}
              value={value.toString()}
              onChange={(e) => {
                if (e.target.value === "") setFieldValue(0)
                if (isNaN(parseFloat(e.target.value))) return
                const value = parseFloat(e.target.value)
                if (value > 99999) setFieldValue(99999)
                else setFieldValue(value)
              }}
              onFocus={(e) => e.target.addEventListener("wheel", (e) => e.preventDefault(), { passive: false })}
            />
            <InputGroupAddon align="inline-end" className="flex items-center gap-1">
              <Text fontStyle="BM" className="pb-0.5">
                {getScoreUnit(ScoreType.WEIGHT, unit)}
              </Text>
              {isScoreSetReferenceForScoreboard && <TrophyIcon color="currentColor" size={16} />}
            </InputGroupAddon>
          </InputGroup>
        </InputContainer>
      )
    case ScoreType.TIME:
      return (
        <div className="flex flex-col gap-2">
          <div className="flex flex-row items-center justify-between px-5">
            <Text fontStyle="BS">{word("session.score.form.timeCapExceeded.label")}</Text>
            <Switch
              checked={formikValue.timeCapExceeded ?? false}
              onCheckedChange={() => {
                void formik.setFieldValue(
                  `scoreSetCompletions.${scoreSetCompletionIndex}.timeCapExceeded`,
                  !formikValue.timeCapExceeded,
                )
                void formik.setFieldValue(formikValueName, 0)
              }}
            />
          </div>

          {formikValue.timeCapExceeded ? (
            <InputContainer isRequiredInput={false} error={getFormErrorMessage(formikValueName, formik)}>
              <InputGroup className="justify-between bg-surface-background pr-2" id={formikValueName}>
                <InputGroupInput
                  name={formikValueName}
                  placeholder="0"
                  max={99999}
                  type="number"
                  inputMode="numeric"
                  step={0.01}
                  min={0}
                  value={value.toString()}
                  onChange={(e) => {
                    if (e.target.value === "") setFieldValue(0)
                    if (isNaN(parseFloat(e.target.value))) return
                    const value = parseFloat(e.target.value)
                    if (value > 99999) setFieldValue(99999)
                    else setFieldValue(value)
                  }}
                  onFocus={(e) => e.target.addEventListener("wheel", (e) => e.preventDefault(), { passive: false })}
                />
                <InputGroupAddon align="inline-end" className="flex items-center gap-1">
                  <Text fontStyle="BM" className="pb-0.5">
                    {word("session.score.form.timeCapExceeded.missingReps", { count: formikValue.score ?? 0 })}
                  </Text>
                  {isScoreSetReferenceForScoreboard && <TrophyIcon color="currentColor" size={16} />}
                </InputGroupAddon>
              </InputGroup>
            </InputContainer>
          ) : (
            <HUChronoInput
              name={formikValueName}
              label={word("chrono.settings.label.training_time")}
              getFieldProps={(name: string) =>
                formik.getFieldProps(name as ScoreFormikValueInputKey) as FieldInputProps<number>
              }
              setFieldValue={(name: string, value: any) =>
                formik.setFieldValue(name as ScoreFormikValueInputKey, value)
              }
              error={getFormErrorMessage(formikValueName, formik)}
              extraAddon={isScoreSetReferenceForScoreboard ? <TrophyIcon color="currentColor" size={16} /> : undefined}
            />
          )}
        </div>
      )
    case ScoreType.CALORIES:
      return (
        <InputContainer isRequiredInput={false} error={getFormErrorMessage(formikValueName, formik)}>
          <InputGroup className="justify-between bg-surface-background pr-2" id={formikValueName}>
            <InputGroupInput
              name={formikValueName}
              placeholder="0"
              max={99999}
              type="number"
              inputMode="numeric"
              step={1}
              min={0}
              value={value.toString()}
              onChange={(e) => {
                if (e.target.value === "") setFieldValue(0)
                if (isNaN(parseInt(e.target.value))) return
                const value = parseInt(e.target.value)
                if (value > 99999) setFieldValue(99999)
                else setFieldValue(value)
              }}
              onFocus={(e) => e.target.addEventListener("wheel", (e) => e.preventDefault(), { passive: false })}
            />
            <InputGroupAddon align="inline-end" className="flex items-center gap-1">
              <Text fontStyle="BM" className="pb-0.5">
                cal
              </Text>
              {isScoreSetReferenceForScoreboard && <TrophyIcon color="currentColor" size={16} />}
            </InputGroupAddon>
          </InputGroup>
        </InputContainer>
      )
    case ScoreType.DISTANCE:
      return (
        <InputContainer isRequiredInput={false} error={getFormErrorMessage(formikValueName, formik)}>
          <InputGroup className="justify-between bg-surface-background pr-2" id={formikValueName}>
            <InputGroupInput
              name={formikValueName}
              placeholder="0"
              max={99999}
              type="number"
              inputMode="numeric"
              step={unit === "IMPERIAL" ? 0.01 : 1}
              min={0}
              value={value.toString()}
              onChange={(e) => {
                if (e.target.value === "") setFieldValue(0)
                if (unit === "IMPERIAL" ? isNaN(parseFloat(e.target.value)) : isNaN(parseInt(e.target.value))) return
                const value = unit === "IMPERIAL" ? parseFloat(e.target.value) : parseInt(e.target.value)
                if (value > 99999) setFieldValue(99999)
                else setFieldValue(value)
              }}
              onFocus={(e) => e.target.addEventListener("wheel", (e) => e.preventDefault(), { passive: false })}
            />
            <InputGroupAddon align="inline-end" className="flex items-center gap-1">
              <Text fontStyle="BM" className="pb-0.5">
                {getScoreUnit(ScoreType.DISTANCE, unit)}
              </Text>
              {isScoreSetReferenceForScoreboard && <TrophyIcon color="currentColor" size={16} />}
            </InputGroupAddon>
          </InputGroup>
        </InputContainer>
      )
    case ScoreType.WATTS:
      return (
        <InputContainer isRequiredInput={false} error={getFormErrorMessage(formikValueName, formik)}>
          <InputGroup className="justify-between bg-surface-background pr-2" id={formikValueName}>
            <InputGroupInput
              name={formikValueName}
              placeholder="0"
              max={99999}
              type="number"
              inputMode="numeric"
              step={1}
              min={0}
              value={value.toString()}
              onChange={(e) => {
                if (e.target.value === "") setFieldValue(0)
                if (isNaN(parseInt(e.target.value))) return
                const value = parseInt(e.target.value)
                if (value > 99999) setFieldValue(99999)
                else setFieldValue(value)
              }}
              onFocus={(e) => e.target.addEventListener("wheel", (e) => e.preventDefault(), { passive: false })}
            />
            <InputGroupAddon align="inline-end" className="flex items-center gap-1">
              <Text fontStyle="BM" className="pb-0.5">
                W
              </Text>
              {isScoreSetReferenceForScoreboard && <TrophyIcon color="currentColor" size={16} />}
            </InputGroupAddon>
          </InputGroup>
        </InputContainer>
      )
  }
}
